import {useCallback, useEffect, useRef, useState} from 'react';
import {OnboardingService} from "../../service/OnboardingService";

const onboardingService = new OnboardingService();

// Default poll interval. Async-first design: the backend computes status from
// data (no webhook), so we poll while the user is on the page.
const DEFAULT_POLL_MS = 60000;

/**
 * Live onboarding state hook.
 * - Fetches status for the given campaign.
 * - Polls every `pollMs` (pauses when the tab is hidden).
 * - Exposes manual `refresh()` and `advance()` / `setStep()` actions.
 */
export function useOnboardingState(campaignId, pollMs = DEFAULT_POLL_MS) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const timerRef = useRef(null);

    const fetchStatus = useCallback((opts = {}) => {
        if (!campaignId) return Promise.resolve(null);
        if (opts.showLoading) setLoading(true);
        return onboardingService.getStatus(campaignId)
            .then(data => {
                setStatus(data);
                setError(null);
                setLastUpdated(new Date());
                return data;
            })
            .catch(err => {
                setError(err);
                return null;
            })
            .finally(() => setLoading(false));
    }, [campaignId]);

    const refresh = useCallback(() => fetchStatus({showLoading: false}), [fetchStatus]);

    const setStep = useCallback((state) => {
        if (!campaignId) return Promise.resolve(null);
        return onboardingService.setState(campaignId, state).then(data => {
            setStatus(data);
            setLastUpdated(new Date());
            return data;
        });
    }, [campaignId]);

    const advance = useCallback((payload) => {
        if (!campaignId) return Promise.resolve(null);
        return onboardingService.advance(campaignId, payload).then(data => {
            setStatus(data);
            setLastUpdated(new Date());
            return data;
        });
    }, [campaignId]);

    const restart = useCallback(() => {
        if (!campaignId) return Promise.resolve(null);
        return onboardingService.restart(campaignId).then(data => {
            setStatus(data);
            setLastUpdated(new Date());
            return data;
        });
    }, [campaignId]);

    // initial load + polling
    useEffect(() => {
        if (!campaignId) return undefined;
        fetchStatus({showLoading: true});

        const startTimer = () => {
            stopTimer();
            timerRef.current = setInterval(() => {
                if (!document.hidden) fetchStatus({showLoading: false});
            }, pollMs);
        };
        const stopTimer = () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };

        startTimer();
        const onVisibility = () => {
            if (!document.hidden) fetchStatus({showLoading: false});
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            stopTimer();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [campaignId, pollMs, fetchStatus]);

    return {status, loading, error, lastUpdated, refresh, setStep, advance, restart};
}
